import { useEffect, useState } from "react";
import { generateCourtReport, getCustody, fileUrl } from "@/lib/api";
import { ConfidenceMeter } from "@/components/ConfidenceMeter";
import { Scale, ShieldCheck, Gavel, FileText, Loader2, Download, Link2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const RISK = { high: "text-monk-critical border-monk-critical/40 bg-monk-critical/10", medium: "text-monk-warn border-monk-warn/40 bg-monk-warn/10", low: "text-neutral-400 border-white/15 bg-white/5" };

function Card({ icon: Icon, title, tag, children, right }) {
  return (
    <div className="bg-monk-panel border border-white/10 rounded-md flex flex-col">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Icon className="w-4 h-4 text-monk-blue" />
        <div className="leading-none flex-1"><div className="font-heading font-700 text-sm tracking-tight">{title}</div><div className="overline mt-1">{tag}</div></div>
        {right}
      </div>
      <div className="p-4 flex-1">{children}</div>
    </div>
  );
}

function CourtReportCard({ caseId, caseData, onUpdated }) {
  const [running, setRunning] = useState(false);
  const [tick, setTick] = useState(0);
  const report = caseData?.court_report;

  const gen = async () => {
    setRunning(true); setTick(0);
    try {
      await generateCourtReport(caseId, (t) => setTick(t));
      toast.success("Court report generated");
      await onUpdated?.();
    } catch { toast.error("Court report could not be generated"); }
    finally { setRunning(false); }
  };

  return (
    <Card icon={Gavel} title="Court Mode Report" tag="§18/19 · Dissenting Opinions"
      right={<button onClick={gen} disabled={running} data-testid="court-report-btn"
        className="flex items-center gap-1.5 text-[11px] font-mono border border-white/15 text-neutral-300 rounded-sm px-2.5 py-1.5 hover:text-white hover:border-monk-blue/50 disabled:opacity-60 transition-colors">
        {running ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Compiling… {tick}s</> : <><FileText className="w-3 h-3" /> {report ? "Regenerate" : "Generate"}</>}
      </button>}>
      {!report ? (
        <p className="hunch-data text-[13px]">Compile a court-admissible PDF with certified findings, mandatory dissenting opinions, and a chain-of-custody attestation.</p>
      ) : (
        <div className="space-y-3 text-[13px]" data-testid="court-report-ready">
          <a href={fileUrl(report.file_id)} target="_blank" rel="noreferrer" data-testid="court-report-download"
            className="inline-flex items-center gap-2 bg-monk-blue text-white rounded-sm px-3 py-2 text-[12px] font-medium hover:bg-monk-blue/90 transition-colors">
            <Download className="w-4 h-4" /> Download PDF Report
          </a>
          <div><div className="overline mb-1">Primary Theory</div><p className="hard-data text-neutral-200 leading-relaxed">{report.report?.primary_theory}</p></div>
          <div>
            <div className="overline mb-1 text-monk-critical">Dissenting Opinions · {(report.report?.dissenting_opinions || []).length}</div>
            <div className="space-y-2">
              {(report.report?.dissenting_opinions || []).slice(0, 3).map((d, i) => (
                <div key={i} className="border-l-2 border-monk-critical/50 pl-3">
                  <p className="hard-data text-neutral-200">{d.contradicting_evidence}</p>
                  <p className="hunch-data text-[12px] mt-0.5">Outweighed: {d.why_outweighed}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="font-mono text-[10px] text-neutral-500 pt-2 border-t border-white/10">CUSTODY ROOT: {report.root_hash?.slice(0, 32)}… · {report.custody_count} entries</div>
        </div>
      )}
    </Card>
  );
}

function CustodyCard({ caseId }) {
  const [ledger, setLedger] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { getCustody(caseId).then(setLedger).catch(() => setErr(true)); }, [caseId]);
  return (
    <Card icon={ShieldCheck} title="Blockchain Custody Trail" tag="§8 · Tamper-evident"
      right={ledger && <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${ledger.verified ? "border-emerald-400/40 text-emerald-400 bg-emerald-400/10" : "border-monk-critical/40 text-monk-critical bg-monk-critical/10"}`}>{ledger.verified ? "INTEGRITY VERIFIED" : "TAMPERED"}</span>}>
      {err ? <p className="text-monk-critical text-[13px]">Could not load the custody ledger.</p> : !ledger ? <p className="hunch-data text-[13px]">Loading ledger…</p> : (
        <div data-testid="custody-ledger">
          <div className="space-y-0 max-h-64 overflow-auto">
            {ledger.entries.map((e) => (
              <div key={e.index} className="flex items-start gap-3 py-1.5 border-b border-white/5 last:border-0">
                <span className="font-mono text-[10px] text-monk-blue w-6 shrink-0">#{e.index}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-neutral-200">{e.action}</div>
                  <div className="font-mono text-[9px] text-neutral-600 flex items-center gap-1"><Link2 className="w-2.5 h-2.5" />{e.hash.slice(0, 24)}…</div>
                </div>
                <span className="overline shrink-0">{e.actor}</span>
              </div>
            ))}
          </div>
          <div className="font-mono text-[10px] text-neutral-500 pt-2 mt-2 border-t border-white/10">ROOT: {ledger.root_hash.slice(0, 40)}…</div>
        </div>
      )}
    </Card>
  );
}

function AdversarialCard({ caseData }) {
  const r = caseData?.case_engines?.adversarial_engine;

  return (
    <Card icon={Scale} title="Adversarial Engine" tag="§23 · Defense Attorney Check"
      right={!r && <span className="text-[9px] font-mono text-neutral-600" data-testid="pending-adversarial_engine">PENDING</span>}>
      {!r ? <p className="hunch-data text-[13px]">Awaiting auto-analysis — a relentless defense attorney stress-tests the case automatically: weak links, hostile cross-examination, and a robustness score.</p> : (
        <div className="space-y-3 text-[13px]" data-testid="adversarial-result">
          <div className="flex items-center gap-3">
            <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${RISK[r.verdict_risk] || RISK.low}`}>VERDICT RISK: {(r.verdict_risk || "—").toUpperCase()}</span>
            <div className="flex-1"><div className="overline mb-0.5">Case Robustness</div><ConfidenceMeter value={r.overall_robustness_percent || 0} /></div>
          </div>
          {(r.weaknesses || []).length > 0 && <div><div className="overline mb-1">Weak Links</div><div className="space-y-1.5">{r.weaknesses.map((w, i) => (
            <div key={i} className="flex items-start gap-2"><span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border shrink-0 ${RISK[w.severity] || RISK.low}`}>{(w.severity || "").toUpperCase()}</span><div><span className="hard-data text-neutral-200">{w.link}</span> <span className="hunch-data">— {w.attack}</span></div></div>
          ))}</div></div>}
          {(r.cross_examination || []).length > 0 && <div><div className="overline mb-1">Simulated Cross-Examination</div><div className="space-y-1.5">{r.cross_examination.slice(0, 4).map((c, i) => (
            <div key={i}><p className="text-neutral-100">Q: {c.question}</p><p className="hunch-data text-[12px]">↳ {c.exposure}</p></div>
          ))}</div></div>}
          {r.monk_note && <div className="flex items-start gap-2 border-l-2 border-monk-blue/50 pl-3"><span className="overline text-monk-blue shrink-0 mt-0.5">Monk</span><p className="text-neutral-200">{r.monk_note}</p></div>}
        </div>
      )}
    </Card>
  );
}

export function LegalCustodyPanel({ caseId, caseData, onUpdated }) {
  return (
    <div className="mt-8" data-testid="legal-custody-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="overline">Legal & Custody</div>
        <div className="h-px flex-1 bg-white/10" />
        <span className="font-heading font-700 text-sm tracking-tight text-neutral-400">Court Mode · Chain of Custody · Adversarial Review</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CourtReportCard caseId={caseId} caseData={caseData} onUpdated={onUpdated} />
        <CustodyCard caseId={caseId} />
        <div className="lg:col-span-2"><AdversarialCard caseData={caseData} /></div>
      </div>
    </div>
  );
}
